/**
 * NextActionButton — the contextual "do the next thing" button shown under the
 * lifecycle band in the AccountDetail header.
 *
 * It maps the account's current sub-stage to the single most relevant forward
 * action (generate a proposal, submit/approve the RFC, send the MSA, advance the
 * CBS handoff, …) and wires it to the matching handler. Stages whose forward
 * move is owned elsewhere (RFC generation lives in the header's "Generate RFC"
 * button; MSA signature is awaited via DocuSign webhook; group creation flips an
 * account ACTIVE) intentionally render nothing here.
 *
 * Admin-only steps (CBS handoff / checklist) are hidden unless the viewer
 * holds `rfc.cbs_queue`. Approval steps are role-gated: brokers see "Approve
 * as broker" — as do client users holding `workflow.approve_rfc`, who may take
 * that step as an override — and only those approve_rfc holders see the client
 * "Approve RFC" (internal admins with tenant.access_all, who can act in any
 * role, still see both).
 */

import React from 'react';
import {
  ArrowRight,
  CheckCircle2,
  FileSignature,
  FileText,
  Send,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import type {AccountsSubStage} from '@api/generated/client';
import {isNextActionBlocked, type NextActionCaps} from './lifecyclePermissions';

type CbsTarget =
  | 'WELCOME_CALL'
  | 'CENSUS_BUILD_OUT'
  | 'CENSUS_REVIEW'
  | 'CENSUS_UPDATE_NEEDED'
  | 'CENSUS_APPROVED'
  | 'IMPLEMENTATION_CALL'
  | 'DRY_RUN_1'
  | 'FINANCIAL_CALL'
  | 'DRY_RUN_2'
  | 'GO_LIVE_RUN';

export interface NextActionHandlers {
  onGenerateProposal: () => void;
  /**
   * Opens the Generate-proposal modal (Quick Proposal vs Informed Analysis) for
   * the post-RFC-approval Proposal step. Distinct from `onGenerateProposal`,
   * which opens the in-page generator for the legacy LEAD proposal CTAs.
   */
  onGenerateProposalModal: () => void;
  onTransitionSubStage: (target: AccountsSubStage) => void;
  onGenerateRfc: () => void;
  onSubmitRfc: () => void;
  onSendToClient: () => void;
  onClientApprove: () => void;
  onBrokerApprove: () => void;
  onSendMsa: () => void;
  onSubmitToCbs: () => void;
  /** Second leg of the two-step CBS handoff (opt-in tenants only). */
  onBeginImplementation: () => void;
  onAdvanceCbs: (target: CbsTarget) => void;
}

interface NextActionButtonProps extends NextActionCaps {
  subStage: AccountsSubStage;
  isArchived: boolean;
  isPending: boolean;
  /** Whether an RFC already exists — hides the "Generate RFC" prospect CTA. */
  hasRfc: boolean;
  /**
   * Whether the broker-approval sub-stage is part of this tenant's pipeline.
   * When false (e.g. Synrgy's client-only approval gate), a submitted RFC is the
   * client admin's "Review and approve" step — brokers can't approve it.
   */
  brokerApprovalEnabled: boolean;
  /**
   * Whether this account's tenant has the "Welcome Call" implementation step
   * (Synrgy/Quantum default-on). Picks the RFC Received forward action: Hold
   * welcome call vs. straight into the census build-out.
   */
  welcomeCallEnabled: boolean;
  /**
   * Whether any MSA has actually gone out (a DocuSign envelope exists or the
   * legacy/manual `msa` record is set). An account can sit on MSA_SENT with
   * nothing sent — Synrgy's proposal save/skip lands directly on "MSA Package
   * Sent" — so the "Go to MSA" CTA stays up until a real send happens.
   */
  msaSent: boolean;
  handlers: NextActionHandlers;
  /**
   * Render just the compact button (no "Next action:" label/row) so it can sit
   * inline next to the current step inside the lifecycle band.
   */
  inline?: boolean;
}

interface NextActionDescriptor {
  label: string;
  icon: LucideIcon;
  run: () => void;
  adminOnly?: boolean;
  /**
   * Visible to brokers, client `workflow.approve_rfc` holders (their override
   * path), and internal admins.
   */
  brokerOnly?: boolean;
  /** Only visible to `workflow.approve_rfc` holders (and internal admins). */
  clientOnly?: boolean;
  /**
   * Badge text shown to a broker who can't take this (admin-owned) step, in place
   * of the button — e.g. "Awaiting admin approval" on a submitted RFC whose tenant
   * has no broker-approval gate.
   */
  brokerWaitingBadge?: string;
}

function describeNextAction(
  subStage: AccountsSubStage,
  hasRfc: boolean,
  brokerApprovalEnabled: boolean,
  welcomeCallEnabled: boolean,
  h: NextActionHandlers,
): NextActionDescriptor | null {
  switch (subStage) {
    case 'NEW_LEAD':
      return {label: 'Generate a proposal', icon: Wand2, run: h.onGenerateProposal};
    case 'PROPOSAL_SENT':
      // Quick proposal generated — the next step is the Informed Analysis, which
      // advances the prospect to "Informed Analysis generated". Opens the same
      // generator (Proposals tab); the Informed Analysis is selected there.
      return {
        label: 'Generate informed proposal with census',
        icon: Wand2,
        run: h.onGenerateProposal,
      };
    case 'PROPOSAL_PRESENTATION':
      // Informed Analysis generated — generate the RFC to enter Onboarding.
      // Hidden once an RFC already exists (the account has moved on).
      return hasRfc ? null : {label: 'Generate RFC', icon: FileText, run: h.onGenerateRfc};
    case 'DRAFT':
      return {label: 'Send to client', icon: Send, run: h.onSendToClient};
    case 'SUBMITTED':
      // Who owns a submitted RFC depends on the tenant's pipeline: with a broker-
      // approval gate the broker approves first; without one (e.g. Synrgy) it's the
      // client admin's "Review and approve" step and brokers only wait.
      return brokerApprovalEnabled
        ? {label: 'Approve as broker', icon: CheckCircle2, run: h.onBrokerApprove, brokerOnly: true}
        : {
            label: 'Review and approve',
            icon: CheckCircle2,
            run: h.onClientApprove,
            clientOnly: true,
            brokerWaitingBadge: 'Awaiting admin approval',
          };
    case 'BROKER_REVIEW':
      return {
        label: 'Approve as broker',
        icon: CheckCircle2,
        run: h.onBrokerApprove,
        brokerOnly: true,
      };
    case 'BROKER_APPROVED':
    case 'CLIENT_REVIEW':
      // After the broker approves, the client admin approves directly — there
      // is no internal-admin handoff in the client portal.
      return {
        label: 'Approve RFC',
        icon: CheckCircle2,
        run: h.onClientApprove,
        clientOnly: true,
      };
    case 'PROPOSAL':
      // Post-RFC-approval Proposal step (Synrgy). Opens the Generate-proposal
      // modal (Quick Proposal vs Informed Analysis); generating there launches
      // the Proposals tab in a new browser tab with the chosen type preset.
      return {label: 'Generate proposal', icon: Wand2, run: h.onGenerateProposalModal};
    case 'CLIENT_APPROVED':
    case 'MSA_PENDING':
      // Navigates to the MSA tab — the actual send needs a DocuSign template
      // picked there first.
      return {label: 'Go to MSA', icon: FileSignature, run: h.onSendMsa};
    case 'MSA_SENT':
      // Synrgy's proposal save/skip lands directly on "MSA Package Sent"
      // without anything actually sent, so surface the send CTA (the button
      // opens the MSA tab, where the send lives). Once a package really went
      // out, the component short-circuits before reaching here and renders an
      // "Awaiting signatures" note instead (the DocuSign webhook rolls the
      // account up to MSA_SIGNED when every envelope completes).
      return {label: 'Send MSA package', icon: Send, run: h.onSendMsa};
    case 'MSA_SIGNED':
      // Hands the account off to CBS: lands on Submitted for implementation for
      // tenants with the two-step handoff, straight into Implementation otherwise.
      return {
        label: 'Submit for implementation',
        icon: ArrowRight,
        run: h.onSubmitToCbs,
        adminOnly: true,
      };
    case 'SUBMITTED_FOR_IMPLEMENTATION':
      // Opt-in two-step handoff: the account was submitted to CBS and is resting
      // in Onboarding; an admin advances it into Implementation.
      return {
        label: 'Begin implementation',
        icon: ArrowRight,
        run: h.onBeginImplementation,
        adminOnly: true,
      };
    case 'RFC_RECEIVED':
      // Welcome-call tenants (Synrgy/Quantum) hold a Welcome Call first; every
      // other tenant goes straight into the census build-out.
      return welcomeCallEnabled
        ? {
            label: 'Hold welcome call',
            icon: ArrowRight,
            run: () => h.onAdvanceCbs('WELCOME_CALL'),
            adminOnly: true,
          }
        : {
            label: 'Begin census build out',
            icon: ArrowRight,
            run: () => h.onAdvanceCbs('CENSUS_BUILD_OUT'),
            adminOnly: true,
          };
    case 'WELCOME_CALL':
      return {
        label: 'Begin census build out',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('CENSUS_BUILD_OUT'),
        adminOnly: true,
      };
    case 'CENSUS_BUILD_OUT':
      return {
        label: 'Send census for review',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('CENSUS_REVIEW'),
        adminOnly: true,
      };
    case 'CENSUS_REVIEW':
      return {
        label: 'Approve census',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('CENSUS_APPROVED'),
        adminOnly: true,
      };
    case 'CENSUS_UPDATE_NEEDED':
      return {
        label: 'Re-submit for census review',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('CENSUS_REVIEW'),
        adminOnly: true,
      };
    case 'CENSUS_APPROVED':
      return {
        label: 'Hold implementation call',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('IMPLEMENTATION_CALL'),
        adminOnly: true,
      };
    case 'IMPLEMENTATION_CALL':
      return {
        label: 'Run Dry Run #1',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('DRY_RUN_1'),
        adminOnly: true,
      };
    case 'DRY_RUN_1':
      return {
        label: 'Hold financial call',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('FINANCIAL_CALL'),
        adminOnly: true,
      };
    case 'FINANCIAL_CALL':
      return {
        label: 'Run Dry Run #2',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('DRY_RUN_2'),
        adminOnly: true,
      };
    case 'DRY_RUN_2':
      return {
        label: 'Go live',
        icon: ArrowRight,
        run: () => h.onAdvanceCbs('GO_LIVE_RUN'),
        adminOnly: true,
      };
    // GO_LIVE_RUN → group creation flow flips the account ACTIVE (no button here)
    // ACTIVE → no further stages
    default:
      return null;
  }
}

export function NextActionButton({
  subStage,
  isInternalAdmin,
  isBroker,
  isClientAdmin,
  canDriveCbs,
  isArchived,
  isPending,
  hasRfc,
  brokerApprovalEnabled,
  welcomeCallEnabled,
  msaSent,
  handlers,
  inline = false,
}: NextActionButtonProps): React.ReactElement | null {
  if (isArchived) return null;

  // A fully sent MSA package has no forward action for anyone — the DocuSign
  // webhook advances the account when every envelope completes. Show a status
  // note where the button would go so the step doesn't look stalled.
  if (subStage === 'MSA_SENT' && msaSent) {
    const waiting = (
      <span className='text-sm italic text-muted-foreground'>Awaiting signatures</span>
    );
    if (inline) return waiting;
    return (
      <div className='mt-4 flex flex-wrap items-center gap-2'>
        <span className='text-sm font-medium text-foreground'>Next action:</span>
        {waiting}
      </div>
    );
  }

  const action = describeNextAction(
    subStage,
    hasRfc,
    brokerApprovalEnabled,
    welcomeCallEnabled,
    handlers,
  );
  if (!action) return null;

  const cannotAct = isNextActionBlocked(action, {
    isInternalAdmin,
    isBroker,
    isClientAdmin,
    canDriveCbs,
  });

  if (cannotAct) {
    // A broker who can't take an admin-owned approval step sees a waiting badge
    // (when the step defines one) instead of nothing.
    if (action.brokerWaitingBadge && isBroker) {
      const badge = <Badge variant='secondary'>{action.brokerWaitingBadge}</Badge>;
      if (inline) return badge;
      return (
        <div className='mt-4 flex flex-wrap items-center gap-2'>
          <span className='text-sm font-medium text-foreground'>Next action:</span>
          {badge}
        </div>
      );
    }
    return null;
  }

  const Icon = action.icon;
  const button = (
    <Button size='sm' disabled={isPending} onClick={action.run}>
      <Icon className='mr-1.5 h-4 w-4' />
      {action.label}
    </Button>
  );

  if (inline) return button;

  return (
    <div className='mt-4 flex flex-wrap items-center gap-2'>
      <span className='text-sm font-medium text-foreground'>Next action:</span>
      {button}
    </div>
  );
}
